/* ==================================================
  c_boost-staff-labels.js — Boost 商品カードの「Web限定」「一部Web限定」ラベル（客注モード専用）
  spec: 03_客注モード_FE §2.6.3 実装メモ / §2.6.7

  - 客注モード（CStaffSession のトークンあり）のときだけ動く。通常の EC では通信もしない。
  - Boost はバリアントのメタフィールドを検索インデックスに持てないため、表示中のカードの
    判定データをテーマの代替テンプレートからまとめて取得する:
      /search?type=product&view=web-limited&q=handle:A OR handle:B …（1 回あたり最大 250 商品）
    検索結果に含まれない商品だけ /products/{handle}?view=web-limited で個別に取得する。
    取得済みの商品は再取得しない（ハンドル単位のキャッシュ）。失敗した商品はラベルを出さない。
  - 判定と表示は window.CWebLimited（テーマの商品カードと同じ判定・同じ位置）。当日日付は JST。
  - Boost 側のテンプレートは変更しない。カードの data-product（handle を含む）だけを使う。
  - カードの購入ボタン（バリエーション選択ポップアップ内の「カートに追加」）は、選択中の SKU が対象で在庫があるなら
    無効化して「Web限定」にする（spec §2.6.5 の Boost の行）。選択中の SKU が在庫切れなら対象でも Boost の売り切れ表示のまま。
    全 SKU が対象の商品は、選択が読めないときも無効化する。ただし Boost が先に無効化しているボタン（売り切れ）には触らない。
    一部の SKU だけが対象の商品は、ポップアップで選ばれているオプション値（Boost が付ける --selected の label の文言）と
    /products/{handle}.js のバリアント一覧から選択中の SKU を求めて判定する。バリアント一覧はポップアップが開いたときだけ
    取得する（Boost はポップアップを全カードに非表示で描画しておくため）。選択が読めないときはボタンを触らず、
    カート追加の制限（fetch）で止める。どのバリアントにも当たらない組み合わせは売り切れと同じく無効のままにする。
  - Boost はカードを遅れて描画したり、同じノードの中身を後から差し替えたりする。カードの追加だけでなく
    カード内部の変化も拾い、さらに一定時間は定期的に見直して、描画のタイミングに依存せず確実に付ける。
  ===================================================*/
(function () {
  'use strict';

  var SEL_CARD = '.boost-sd__product-item';
  /* 機能タグの行が無いカードでは、ブランド名（無ければ商品名）の直前に行を作る */
  var ANCHOR = '.c_product-item-meta__vendor__wrapper, .boost-sd__product-vendor, .boost-sd__product-title';
  var SEL_ADD_BUTTON = '.boost-sd__btn-add-to-cart';
  var SEL_POPUP = '.boost-sd__popup-select-option';
  var SEL_SWATCH_GROUP = '.boost-sd__product-swatch';
  var SEL_SELECTED_VALUE = '.boost-sd__radio-label--selected';
  var DISABLED_CLASS = 'c_web-limited-disabled';
  var RELABEL_CLASS = 'c_web-limited-relabel';
  var BUTTON_LABEL_ATTR = 'data-web-limited-button-label';
  var SEL_BUTTON_LABEL = '[' + BUTTON_LABEL_ATTR + ']';
  var BATCH_MAX = 250;
  var FALLBACK_CONCURRENCY = 6;
  var SCAN_DELAY_MS = 100;
  var POLL_INTERVAL_MS = 1500;
  var POLL_DURATION_MS = 120000;

  var cache = {};        // handle → 判定データ
  var inFlight = {};     // handle → true（取得中）
  var failed = {};       // handle → true（このページでは再取得しない）
  var variantsOf = {};   // handle → [{ id, options, available }]（ポップアップの選択 SKU を求める用）
  var variantsInFlight = {};
  var variantsFailed = {};
  var stats = { batchRequests: 0, fallbackRequests: 0, variantRequests: 0, failures: 0 };
  var scanTimer = null;

  function api() {
    return window.CWebLimited || null;
  }

  function staffOk() {
    var W = api();
    return !!(W && W.isStaffMode());
  }

  function rootUrl() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root || '/';
  }

  /* カードの商品ハンドル: data-product（JSON）→ 商品リンクの順 */
  function handleOf(card) {
    var raw = card.getAttribute('data-product');
    /* data-product があるときだけキャッシュする（リンクから拾う場合は Boost がノードを使い回すと古くなるため毎回読む） */
    if (raw && card.__cWebLimitedHandleRaw === raw && card.__cWebLimitedHandle) return card.__cWebLimitedHandle;
    var handle = parseHandle(card, raw);
    card.__cWebLimitedHandleRaw = raw;
    card.__cWebLimitedHandle = handle;
    return handle;
  }

  function parseHandle(card, raw) {
    if (raw) {
      try {
        var product = JSON.parse(raw);
        if (product && product.handle) return String(product.handle);
      } catch (e) { /* リンクから拾う */ }
    }
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var m = /\/products\/([^/?#]+)/.exec(link.getAttribute('href') || '');
    return m ? decodeURIComponent(m[1]) : null;
  }

  function paint(card, handle) {
    var W = api();
    var status = handle && cache[handle] ? W.productStatus(cache[handle]) : 'none';
    W.paintCardLabel(card, status, ANCHOR);
    paintAddButtons(card, status, handle);
  }

  function paintAddButtons(card, status, handle) {
    var buttons = card.querySelectorAll(SEL_ADD_BUTTON);
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      if (status !== 'all' && status !== 'partial') {
        releaseAddButton(btn);
        continue;
      }
      var popup = btn.closest ? btn.closest(SEL_POPUP) : null;
      var picked = popup ? selectedVariant(popup, handle) : UNKNOWN;
      if (picked === UNKNOWN) {
        /* 選択が読めない: 全 SKU が対象なら無効化する。Boost が自分で無効化している（売り切れ）ボタンはそのまま */
        if (status === 'all' && !(btn.disabled && !btn.__cWebLimitedOwned)) blockAddButton(btn);
        else releaseAddButton(btn);
      } else if (picked === NO_MATCH) releaseAddButton(btn, NO_MATCH);
      else if (picked.available && api().isVariantRestricted(cache[handle], picked.id)) blockAddButton(btn);
      else releaseAddButton(btn, picked);
    }
  }

  var UNKNOWN = {};                          // 選択かバリアント一覧が読めない
  var NO_MATCH = { available: false };       // 選択は読めたがどのバリアントにも当たらない（Boost 側で無効）

  function norm(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /* 選ばれている label の値。ポップアップの label は文言（前後に改行・空白あり）、無ければ aria-label「名前:値」の値。 */
  function labelValue(label) {
    var text = norm(label.textContent);
    if (text) return text;
    var aria = label.getAttribute('aria-label') || '';
    var sep = aria.indexOf(':');
    return sep === -1 ? '' : norm(aria.slice(sep + 1));
  }

  /* ポップアップで選ばれているオプション値（オプションの並び順）。1 つでも読めなければ null。 */
  function selectedValues(popup) {
    var groups = popup.querySelectorAll(SEL_SWATCH_GROUP);
    if (!groups.length) return null;
    var values = [];
    for (var i = 0; i < groups.length; i++) {
      var picked = groups[i].querySelector(SEL_SELECTED_VALUE);
      var value = picked ? labelValue(picked) : '';
      if (!value) return null;
      values.push(value);
    }
    return values;
  }

  /* Boost は開いたポップアップにだけ display: none を外す */
  function isOpen(popup) {
    return !(popup.style && popup.style.display === 'none');
  }

  /* 選択中のバリアント / NO_MATCH / UNKNOWN。バリアント一覧は開いたポップアップの分だけ取得し、取得後に見直す。 */
  function selectedVariant(popup, handle) {
    var values = handle ? selectedValues(popup) : null;
    if (!values) return UNKNOWN;
    var variants = variantsOf[handle];
    if (!variants) {
      if (isOpen(popup)) loadVariants(handle);
      return UNKNOWN;
    }
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i].options;
      if (opts.length !== values.length) continue;
      var match = true;
      for (var j = 0; j < opts.length && match; j++) match = norm(opts[j]) === values[j];
      if (match) return variants[i];
    }
    return NO_MATCH;
  }

  function loadVariants(handle) {
    if (variantsInFlight[handle] || variantsFailed[handle]) return;
    variantsInFlight[handle] = true;
    stats.variantRequests++;
    fetchText(rootUrl() + 'products/' + encodeURIComponent(handle) + '.js')
      .then(function (text) {
        var product = JSON.parse(text);
        variantsOf[handle] = (product && product.variants || []).map(function (v) {
          return { id: v.id, options: v.options || [], available: v.available !== false };
        });
      })
      .catch(function () {
        variantsFailed[handle] = true;
      })
      .then(function () {
        delete variantsInFlight[handle];
        scheduleScan();
      });
  }

  /* Boost のボタンは Boost 自身が描画・更新するため、子ノードには触らない。
     元の文言は CSS（.c_web-limited-relabel）で隠し、「Web限定」の span を足すだけにする。
     class・span・disabled は毎回付け直す（Boost が再描画で className や disabled を書き換えても、次の見直しで戻る）。
     自分が無効化したかどうかは class ではなくボタン自身のプロパティで覚える。 */
  function blockAddButton(btn) {
    if (!btn.__cWebLimitedOwned) {
      btn.__cWebLimitedOwned = true;
      btn.__cWebLimitedSetDisabled = !btn.disabled;
      btn.__cWebLimitedAria = btn.getAttribute('aria-disabled');
    }
    if (!btn.classList.contains(DISABLED_CLASS)) btn.classList.add(DISABLED_CLASS);
    if (!btn.classList.contains(RELABEL_CLASS)) btn.classList.add(RELABEL_CLASS);
    if (!btn.querySelector(SEL_BUTTON_LABEL)) {
      var label = document.createElement('span');
      label.setAttribute(BUTTON_LABEL_ATTR, '');
      label.textContent = api().labels().all;
      btn.appendChild(label);
    }
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
  }

  /* 足したものだけを外す。disabled・aria-disabled は無効化する前の値に戻す（売り切れなど Boost 側の状態は残す）。
     ポップアップで選び直した SKU が売り切れ（またはどのバリアントにも当たらない）なら、Boost が無効化するはずなので
     文言だけ戻して disabled は残し、次に買える SKU が選ばれたときに戻せるよう無効化前の値は持ち続ける。 */
  function releaseAddButton(btn, variant) {
    var label = btn.querySelector(SEL_BUTTON_LABEL);
    if (!btn.__cWebLimitedOwned && !label) return;
    btn.classList.remove(DISABLED_CLASS);
    btn.classList.remove(RELABEL_CLASS);
    if (label) btn.removeChild(label);
    if (btn.__cWebLimitedOwned && variant && !variant.available) return;
    if (btn.__cWebLimitedOwned) {
      if (btn.__cWebLimitedSetDisabled) btn.disabled = false;
      if (btn.__cWebLimitedAria == null) btn.removeAttribute('aria-disabled');
      else btn.setAttribute('aria-disabled', btn.__cWebLimitedAria);
    }
    btn.__cWebLimitedOwned = false;
    btn.__cWebLimitedSetDisabled = false;
    btn.__cWebLimitedAria = null;
  }

  /* キーボード操作や、Boost が disabled を外してから次の見直しまでの間のクリックも止める */
  function onClick(event) {
    var target = event.target;
    var button = target && target.closest ? target.closest(SEL_ADD_BUTTON) : null;
    if (!button || !button.__cWebLimitedOwned || !staffOk()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function store(handle, data) {
    if (!data) return;
    cache[handle] = data;
    api().register(data);
  }

  function fetchText(url) {
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      });
  }

  function fetchBatch(handles) {
    var q = handles.map(function (h) { return 'handle:' + h; }).join(' OR ');
    var url = rootUrl() + 'search?type=product&view=web-limited&options%5Bunavailable_products%5D=last&q=' + encodeURIComponent(q);
    stats.batchRequests++;
    return fetchText(url).then(function (text) {
      var map = JSON.parse(text);
      return map && typeof map === 'object' ? map : {};
    });
  }

  function fetchOne(handle) {
    stats.fallbackRequests++;
    return fetchText(rootUrl() + 'products/' + encodeURIComponent(handle) + '?view=web-limited')
      .then(function (text) {
        var data = api().parse(text);
        if (!data) throw new Error('invalid web-limited data');
        store(handle, data);
      })
      .catch(function () {
        stats.failures++;
        failed[handle] = true;
      });
  }

  /* 検索結果に含まれなかった商品を同時 FALLBACK_CONCURRENCY 件までで個別取得する */
  function fetchMissing(handles) {
    var queue = handles.slice();
    function worker() {
      var next = queue.shift();
      if (!next) return Promise.resolve();
      return fetchOne(next).then(worker);
    }
    var workers = [];
    for (var i = 0; i < Math.min(FALLBACK_CONCURRENCY, queue.length); i++) workers.push(worker());
    return Promise.all(workers);
  }

  function load(handles) {
    var chunks = [];
    for (var i = 0; i < handles.length; i += BATCH_MAX) chunks.push(handles.slice(i, i + BATCH_MAX));
    return Promise.all(chunks.map(function (chunk) {
      return fetchBatch(chunk)
        .then(function (map) {
          var missing = [];
          chunk.forEach(function (h) {
            if (map[h]) store(h, api().parse(map[h]));
            else missing.push(h);
          });
          return fetchMissing(missing);
        })
        .catch(function () {
          /* まとめての取得が失敗したら個別取得に切り替える */
          return fetchMissing(chunk);
        });
    })).then(function () {
      handles.forEach(function (h) { delete inFlight[h]; });
      scheduleScan();
    });
  }

  function scan() {
    if (!api()) return;
    var cards = document.querySelectorAll(SEL_CARD);
    if (!cards.length) return;
    var staff = staffOk();
    var pending = [];
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var handle = staff ? handleOf(card) : null;
      paint(card, handle);
      if (handle && !cache[handle] && !inFlight[handle] && !failed[handle] && pending.indexOf(handle) === -1) {
        pending.push(handle);
      }
    }
    if (!pending.length) return;
    pending.forEach(function (h) { inFlight[h] = true; });
    load(pending);
  }

  function scheduleScan() {
    if (scanTimer) return;
    scanTimer = setTimeout(function () {
      scanTimer = null;
      scan();
    }, SCAN_DELAY_MS);
  }

  function touchesCard(mutation) {
    var target = mutation.target;
    /* class はポップアップでのオプション選択（--selected の付け替え）、style はポップアップの開閉だけを拾う */
    if (mutation.type === 'attributes' && (mutation.attributeName === 'class' || mutation.attributeName === 'style')) {
      if (!target || target.nodeType !== 1 || !target.matches) return false;
      if (mutation.attributeName === 'style') return target.matches(SEL_POPUP);
      return target.matches('.boost-sd__radio-label') && !!(target.closest && target.closest(SEL_POPUP));
    }
    if (target && target.nodeType === 1 && target.closest && target.closest(SEL_CARD)) return true;
    var nodes = mutation.addedNodes || [];
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.nodeType !== 1) continue;
      if ((node.matches && node.matches(SEL_CARD)) || (node.querySelector && node.querySelector(SEL_CARD))) return true;
    }
    return false;
  }

  function registerBoostHook(attempt) {
    if (typeof boostWidgetIntegration !== 'undefined' && boostWidgetIntegration.regisCustomization) {
      boostWidgetIntegration.regisCustomization(scheduleScan, 'filter');
      return;
    }
    if (attempt < 40) setTimeout(function () { registerBoostHook(attempt + 1); }, 500);
  }

  function init() {
    if (!staffOk()) return;
    document.addEventListener('click', onClick, true);
    scan();
    if (typeof MutationObserver === 'function' && document.body) {
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (touchesCard(mutations[i])) {
            scheduleScan();
            return;
          }
        }
      }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-product', 'class', 'style'] });
    }
    registerBoostHook(0);
    /* 描画のタイミングを取りこぼしても付くよう、Boost の表示が落ち着くまで見直す（冪等） */
    var started = Date.now();
    var poll = setInterval(function () {
      if (Date.now() - started > POLL_DURATION_MS) {
        clearInterval(poll);
        return;
      }
      scan();
    }, POLL_INTERVAL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) scan();
  });
  document.addEventListener('shopify:section:load', scan);

  window.debugBoostStaffLabels = {
    scan: scan,
    stats: function () {
      return {
        cards: document.querySelectorAll(SEL_CARD).length,
        painted: document.querySelectorAll(SEL_CARD + ' [data-web-limited-label]').length,
        cached: Object.keys(cache).length,
        failed: Object.keys(failed).length,
        batchRequests: stats.batchRequests,
        fallbackRequests: stats.fallbackRequests,
        variantRequests: stats.variantRequests,
        failures: stats.failures
      };
    }
  };
})();
